import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

// ---------------------------------------------------------------------------
// Shared axios instance with base URL
// ---------------------------------------------------------------------------
const api = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ---------------------------------------------------------------------------
// Auth-token interceptor (extracted from axiosPlugin.js)
// ---------------------------------------------------------------------------
api.interceptors.request.use(
  async (config) => {
    let retryCount = 0;
    const MAX_RETRIES = 3;
    const token = localStorage.getItem('token');
    if (token) {
      const decodedToken = jwtDecode(token);
      const isExpired = decodedToken.exp * 1000 < Date.now();

      if (isExpired) {
        if (retryCount >= MAX_RETRIES) {
          retryCount = 0;
          localStorage.removeItem('token');
          // Router push isn't available here directly; redirect via window
          window.location.href = '/login';
          return Promise.reject(new Error('Token refresh retry limit exceeded'));
        }
        retryCount++;
        try {
          const refreshToken = localStorage.getItem('refresh_token');
          const refreshResponse = await api.post('/api/token/refresh/', {
            refresh: refreshToken,
          });

          const newAccessToken = refreshResponse.data.access;
          localStorage.setItem('token', newAccessToken);
          config.headers.Authorization = `Bearer ${newAccessToken}`;
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          localStorage.removeItem('token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ---------------------------------------------------------------------------
// Helper: attach auth header manually where JWT is required
// ---------------------------------------------------------------------------
function authHeader() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ===========================================================================
// Auth
// ===========================================================================
export function login(username, password) {
  return api.post('/api/token/', { username, password });
}

export function refreshToken(refreshTokenValue) {
  return api.post('/api/token/refresh/', { refresh: refreshTokenValue });
}

export function validateField(field, value) {
  return api.get('/api/users/validate-field/', { params: { field, value } });
}

export function registerUser(data) {
  return api.post('/api/users/', data);
}

// ===========================================================================
// User / Profile
// ===========================================================================
export function fetchProfile() {
  return api.get('/api/users/me/', { headers: authHeader() });
}

export function updateProfile(payload) {
  return api.patch('/api/users/me/', payload, { headers: authHeader() });
}

export function uploadAvatar(file) {
  const formData = new FormData();
  formData.append('avatar', file);
  return api.patch('/api/users/me/', formData, {
    headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
  });
}

// ===========================================================================
// Author Profile
// ===========================================================================
export function fetchAuthorProfile() {
  return api.get('/api/author-profiles/me/', { headers: authHeader() });
}

export function createAuthorProfile(data) {
  return api.post('/api/author-profiles/', data, { headers: authHeader() });
}

export function updateAuthorProfile(payload) {
  return api.patch('/api/author-profiles/me/', payload, { headers: authHeader() });
}

export function uploadAuthorPhoto(file) {
  const formData = new FormData();
  formData.append('photo', file);
  return api.patch('/api/author-profiles/me/', formData, {
    headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
  });
}

// ===========================================================================
// Books
// ===========================================================================
export function fetchBooks(params = {}) {
  return api.get('/api/books/', { params });
}

export function fetchBook(slug) {
  return api.get(`/api/books/${slug}/`);
}

export function fetchMyBooks() {
  return api.get('/api/books/my-books/', { headers: authHeader() });
}

export function createBook(formData) {
  return api.post('/api/books/', formData, {
    headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
  });
}

export function updateBook(slug, formData) {
  return api.put(`/api/books/${slug}/`, formData, {
    headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
  });
}

export function publishBook(slug) {
  return api.patch(
    `/api/books/${slug}/publish/`,
    {},
    { headers: authHeader() },
  );
}

export function unpublishBook(slug) {
  return api.patch(
    `/api/books/${slug}/unpublish/`,
    {},
    { headers: authHeader() },
  );
}

export function deleteBook(slug) {
  return api.delete(`/api/books/${slug}/`, { headers: authHeader() });
}

// ===========================================================================
// Chapters
// ===========================================================================
export function fetchChapter(slug) {
  return api.get(`/api/chapters/${slug}/`);
}

export function createChapter(data) {
  return api.post('/api/chapters/', data, { headers: authHeader() });
}

export function updateChapter(slug, data) {
  return api.put(`/api/chapters/${slug}/`, data, { headers: authHeader() });
}

export function deleteChapter(slug) {
  return api.delete(`/api/chapters/${slug}/`, { headers: authHeader() });
}

export function fetchChapterComments(chapterSlug) {
  return api.get(`/api/chapters/${chapterSlug}/comments/`);
}

export function createComment(chapterSlug, content) {
  return api.post(
    `/api/chapters/${chapterSlug}/create-comment/`,
    { content },
    { headers: authHeader() },
  );
}

// ===========================================================================
// Reading Sessions
// ===========================================================================
export function startReadingSession(chapterId, deviceInfo, sessionId) {
  return api.post(
    '/api/reading-sessions/start_session/',
    { chapter_id: chapterId, device_info: deviceInfo, session_id: sessionId },
    { headers: authHeader() },
  );
}

export function validateReadingSession(chapterId, sessionId) {
  return api.post(
    '/api/reading-sessions/validate_session/',
    { chapter_id: chapterId, session_id: sessionId },
    { headers: authHeader() },
  );
}

// ===========================================================================
// Reading Duration & Stats
// ===========================================================================
export function updateReadDuration(payload) {
  return api.post('/api/chapters/update-read-duration/', payload, {
    headers: authHeader(),
  });
}

export function fetchReadingStats() {
  return api.get('/api/reading-stats/', { headers: authHeader() });
}

// ===========================================================================
// My Reads
// ===========================================================================
export function fetchMyReads(url) {
  const requestUrl = url || '/api/my-reads/';
  return api.get(requestUrl, { headers: authHeader() });
}

// ===========================================================================
// Categories
// ===========================================================================
export function fetchCategories() {
  return api.get('/api/book-categories/');
}

export function fetchFictionCategories() {
  return api.get('/api/book-categories/fiction/');
}

export function fetchNonFictionCategories() {
  return api.get('/api/book-categories/non-fiction/');
}

export function fetchCategory(id) {
  return api.get(`/api/book-categories/${id}/`);
}

export function fetchSubcategories(categoryId) {
  return api.get(`/api/book-categories/${categoryId}/subcategories/`);
}

export function fetchCategoryBookCounts() {
  return api.get('/api/book-categories/book-counts/');
}

export function fetchCategoryTotalCounts() {
  return api.get('/api/book-categories/total-counts/');
}

// ===========================================================================
// Collections / Bookmarks
// ===========================================================================
export function fetchCollections() {
  return api.get('/api/collections/', { headers: authHeader() });
}

export function fetchDefaultCollection() {
  return api.get('/api/collections/default/', { headers: authHeader() });
}

export function fetchCollection(id) {
  return api.get(`/api/collections/${id}/`, { headers: authHeader() });
}

export function createCollection(data) {
  return api.post('/api/collections/', data, { headers: authHeader() });
}

export function updateCollection(id, data) {
  return api.put(`/api/collections/${id}/`, data, { headers: authHeader() });
}

export function deleteCollection(id) {
  return api.delete(`/api/collections/${id}/`, { headers: authHeader() });
}

export function addBookToCollection(collectionId, data) {
  return api.post(`/api/collections/${collectionId}/add-book/`, data, {
    headers: authHeader(),
  });
}

export function removeBookFromCollection(collectionId, bookmarkId) {
  return api.delete(
    `/api/collections/${collectionId}/remove-book/${bookmarkId}/`,
    { headers: authHeader() },
  );
}

export function updateBookmark(bookmarkId, data) {
  return api.patch(`/api/bookmarks/${bookmarkId}/`, data, {
    headers: authHeader(),
  });
}

// ===========================================================================
// Subscriptions
// ===========================================================================
export function createSubscription(data) {
  return api.post('/api/subscriptions/create/', data, { headers: authHeader() });
}

export function fetchSubscriptionStatus() {
  return api.get('/api/subscriptions/status/', { headers: authHeader() });
}

export function fetchBillingHistory() {
  return api.get('/api/subscriptions/billing-history/', { headers: authHeader() });
}

export function confirmPayment(orderId) {
  return api.post('/api/subscriptions/confirm-payment/', { order_id: orderId }, {
    headers: authHeader(),
  });
}

// ===========================================================================
// Payments
// ===========================================================================
export function fetchPaymentStatus(transactionId) {
  return api.get(`/api/payments/status/${transactionId}/`, { headers: authHeader() });
}

// ===========================================================================
// Referrals
// ===========================================================================
export function fetchMyReferralCode() {
  return api.get('/api/referrals/my-code/', { headers: authHeader() });
}

export function generateReferralCode() {
  return api.post('/api/referrals/generate-code/', {}, { headers: authHeader() });
}

export function fetchReferredUsers() {
  return api.get('/api/referrals/referred-users/', { headers: authHeader() });
}

export function fetchEarningsSummary() {
  return api.get('/api/referrals/earnings/summary/', { headers: authHeader() });
}

export function fetchEarnings() {
  return api.get('/api/referrals/earnings/', { headers: authHeader() });
}

// ===========================================================================
// Bank Accounts
// ===========================================================================
export function fetchBankAccounts() {
  return api.get('/api/bank-accounts/', { headers: authHeader() });
}

export function createBankAccount(data) {
  return api.post('/api/bank-accounts/', data, { headers: authHeader() });
}

export function updateBankAccount(id, data) {
  return api.patch(`/api/bank-accounts/${id}/`, data, { headers: authHeader() });
}

export function deleteBankAccount(id) {
  return api.delete(`/api/bank-accounts/${id}/`, { headers: authHeader() });
}

// ===========================================================================
// Static Pages
// ===========================================================================
export function fetchPageBySlug(slug) {
  return api.get(`/api/pages/by-slug/${slug}/`);
}

// ===========================================================================
// Feedback
// ===========================================================================
export function submitFeedback(data) {
  return api.post('/api/feedback/', data);
}

// ===========================================================================
// Knowledgebase
// ===========================================================================
export function incrementArticleReadCount(articleId) {
  return api.post(`/api/knowledgebase/articles/${articleId}/increment_read_count/`);
}

// ===========================================================================
// Export the raw axios instance for advanced use (e.g. cancel tokens)
// ===========================================================================
export default api;
